"use client"

import { useState, type CSSProperties } from "react"

// SignalMax Careers brand logo — theme-swapped transparent lockups, no background box.
//
// Both are the illustrated transparent lockup on a transparent ground, so neither sits
// in a box: light mode shows the navy-ink lockup (/signalmax-careers-logo-light.png),
// which reads on light surfaces; dark mode shows the white-ink lockup
// (/signalmax-careers-logo-dark.png), which reads on the app's navy canvas. CSS keyed on
// the `.dark` class shows exactly one, so there is no theme flash or hydration mismatch.
// Fails soft to a theme-safe text wordmark if an asset is missing.
//
// `variant` only sets the default height (wordmark 44 for slim headers/nav, lockup 64 for
// hero/auth). Pass `height` to override. Never put `display` in the inline style: the
// `.dark`-keyed CSS classes own display, and an inline value would beat them.
export function CareerLogo({
  variant = "wordmark",
  height,
  href,
}: {
  variant?: "lockup" | "wordmark"
  height?: number
  href?: string
}) {
  const [lightFailed, setLightFailed] = useState(false)
  const [darkFailed, setDarkFailed] = useState(false)
  const h = height ?? (variant === "lockup" ? 64 : 44)
  const imgStyle: CSSProperties = { height: h, width: "auto", maxWidth: "100%" }
  const content = (
    <>
      {lightFailed ? (
        <span className="smx-careers-logo-light"><WordmarkText height={h} /></span>
      ) : (
        <img
          className="smx-careers-logo-light"
          src="/signalmax-careers-logo-light.png"
          alt="SignalMax Careers"
          onError={() => setLightFailed(true)}
          style={imgStyle}
        />
      )}
      {darkFailed ? (
        <span className="smx-careers-logo-dark"><WordmarkText height={h} /></span>
      ) : (
        <img
          className="smx-careers-logo-dark"
          src="/signalmax-careers-logo-dark.png"
          alt="SignalMax Careers"
          onError={() => setDarkFailed(true)}
          style={imgStyle}
        />
      )}
    </>
  )
  if (!href) return content
  return (
    <a href={href} aria-label="SignalMax Careers" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
      {content}
    </a>
  )
}

// Theme-safe fail-soft fallback only: "Signal" + brand-orange "Max" + a "Careers" tag.
function WordmarkText({ height = 44 }: { height?: number }) {
  const fs = Math.round(height * 0.5)
  const wrap: CSSProperties = { display: "inline-flex", alignItems: "baseline", gap: Math.round(fs * 0.42), whiteSpace: "nowrap" }
  return (
    <span style={wrap}>
      <span style={{ fontWeight: 800, fontSize: fs, letterSpacing: "-0.02em", lineHeight: 1, color: "var(--text-primary)" }}>
        Signal<span style={{ color: "var(--accent)" }}>Max</span>
      </span>
      <span style={{ fontSize: Math.max(9, Math.round(fs * 0.44)), fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--text-muted)" }}>Careers</span>
    </span>
  )
}
